<script setup lang="ts">
import { Carousel, Navigation, Slide } from 'vue3-carousel';
import { NuxtLinkLocale } from '#components';
import 'vue3-carousel/carousel.css';
import type { BrandInterface } from '~~/server/types/Brands';
import BrandsSectionCard from './BrandsSectionCard.vue';

const props = defineProps<{
  config: { showHeader?: boolean; headerText?: string; showFooter?: boolean; btnText?: string; design?: string; limit?: number; showName?: boolean; linkToBrand?: boolean };
  class?: string;
}>();
const { $api } = useNuxtApp();
const { t, locale } = useI18n();
const header = computed(() => props.config.headerText ?? t('brands'));
const limit = computed(() => {
  const value = Number(props.config.limit);
  return Number.isFinite(value) && value >= 1 ? Math.floor(value) : 4;
});
const requestedCount = computed(() => props.config.design === 'grid' ? limit.value : Infinity);
const key = computed(() => `section-brands:${locale.value}:${requestedCount.value}`);
const { data: brands } = await useAsyncData(key, async () => {
  const requestedLimit = requestedCount.value;
  const perPage = Math.min(requestedLimit, 100);
  const list: BrandInterface[] = [];
  for (let page = 1; ; page++) {
    const result = await $api<{ list: BrandInterface[]; meta: { last_page: number } }>('brands', {
      query: { page, per_page: perPage },
    });
    list.push(...result.list);
    if (list.length >= requestedLimit || page >= result.meta.last_page || !result.list.length) break;
  }
  return list.slice(0, requestedLimit);
}, { default: () => [] });

const visibleCount = computed(() => Math.min(limit.value, brands.value?.length || 1));
</script>

<template>
  <div v-if="brands?.length" :class="props.class">
    <section class="brands-section">
      <h2 v-if="config.showHeader !== false && header" class="section_title brands-section__title">
        <NuxtLinkLocale to="/brands">{{ header }}</NuxtLinkLocale>
      </h2>
      <Carousel v-if="config.design !== 'grid'" :items-to-show="visibleCount" :items-to-scroll="visibleCount" :gap="16" snap-align="start" :clamp="true" class="brands-carousel">
        <Slide v-for="brand in brands" :key="brand.id">
          <BrandsSectionCard :brand="brand" :show-name="config.showName !== false" :link-to-brand="config.linkToBrand !== false" />
        </Slide>
        <template #addons="{ slidesCount, config: carouselConfig }">
          <Navigation v-if="slidesCount > Number(carouselConfig.itemsToShow)" />
        </template>
      </Carousel>
      <div v-else class="brands-grid">
        <BrandsSectionCard v-for="brand in brands" :key="brand.id" :brand="brand" :show-name="config.showName !== false" :link-to-brand="config.linkToBrand !== false" />
      </div>
      <div v-if="config.showFooter !== false" class="brands-section__footer">
        <NuxtLinkLocale to="/brands" class="el-button el-button--primary el-button--default is-round">{{ config.btnText ?? t('view_all') }}</NuxtLinkLocale>
      </div>
    </section>
  </div>
</template>

<style scoped>
.brands-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
.brands-carousel { --vc-nav-color: var(--el-color-primary); --vc-nav-background: var(--body-bg); --vc-nav-border-radius: 50%; --vc-nav-width: 30px; --vc-nav-height: 30px; }
.brands-carousel :deep(.carousel__slide) { align-items: stretch; }
@media (min-width: 992px) { .brands-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
</style>
